/**
 * OTA Logger
 *
 * Centralized logging utility for OTA update operations.
 * Provides extensive logging for debugging and tracking update lifecycle.
 */

import { OtaRelease, OtaUpdateStatus, OtaErrorCode } from './types';
import type { OtaStatusResponse } from './types';

/**
 * OTA Logger Class
 *
 * Logs all OTA-related events with structured data for debugging.
 * All logs are prefixed with [OTA] for easy filtering.
 */
class OtaLogger {
  private readonly prefix = '[OTA]';

  /**
   * Log service initialization
   */
  init(currentVersion: string, platform: string, deviceId: string): void {
    console.log(`${this.prefix} 🚀 OTA Service initialized`, {
      currentVersion,
      platform,
      deviceId,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log when update check starts
   */
  checkStart(currentVersion: string, platform: string, deviceId: string): void {
    console.log(`${this.prefix} 🔍 Checking for updates...`, {
      currentVersion,
      platform,
      deviceId,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log when update check completes with no update
   */
  checkNoUpdate(currentVersion: string): void {
    console.log(`${this.prefix} ✅ No update available`, {
      currentVersion,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log when an update is available
   */
  updateAvailable(release: OtaRelease, currentVersion: string): void {
    console.log(`${this.prefix} 🆕 Update available!`, {
      currentVersion,
      newVersion: release.version,
      buildNumber: release.buildNumber,
      bundleSize: this.formatBytes(release.bundleSizeBytes),
      bundleSizeBytes: release.bundleSizeBytes,
      isMandatory: release.isMandatory,
      releaseNotes: release.releaseNotes?.substring(0, 100),
      bundleUrl: release.bundleUrl,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log when download starts
   */
  downloadStart(release: OtaRelease): void {
    console.log(`${this.prefix} ⬇️ Download started`, {
      version: release.version,
      bundleUrl: release.bundleUrl,
      bundleSize: this.formatBytes(release.bundleSizeBytes),
      bundleSizeBytes: release.bundleSizeBytes,
      checksum: release.bundleChecksum.substring(0, 16) + '...',
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log download progress
   */
  downloadProgress(
    progress: number,
    bytesDownloaded: number,
    totalBytes: number,
  ): void {
    console.log(
      `${this.prefix} ⬇️ Download progress: ${progress.toFixed(1)}%`,
      {
        progress,
        downloaded: this.formatBytes(bytesDownloaded),
        total: this.formatBytes(totalBytes),
        bytesDownloaded,
        totalBytes,
      },
    );
  }

  /**
   * Log when download completes
   */
  downloadComplete(bundlePath: string, sizeBytes: number): void {
    console.log(`${this.prefix} ✅ Download complete`, {
      bundlePath,
      size: this.formatBytes(sizeBytes),
      sizeBytes,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log checksum verification start
   */
  checksumVerifyStart(expectedChecksum: string): void {
    console.log(`${this.prefix} 🔐 Verifying checksum...`, {
      expectedChecksum: expectedChecksum.substring(0, 16) + '...',
    });
  }

  /**
   * Log checksum verification success
   */
  checksumVerifySuccess(
    calculatedChecksum: string,
    expectedChecksum: string,
  ): void {
    console.log(`${this.prefix} ✅ Checksum verified`, {
      calculated: calculatedChecksum.substring(0, 16) + '...',
      expected: expectedChecksum.substring(0, 16) + '...',
      match: true,
    });
  }

  /**
   * Log checksum verification failure
   */
  checksumVerifyFailed(
    calculatedChecksum: string,
    expectedChecksum: string,
  ): void {
    console.error(`${this.prefix} ❌ Checksum mismatch!`, {
      calculated: calculatedChecksum.substring(0, 16) + '...',
      expected: expectedChecksum.substring(0, 16) + '...',
      match: false,
      fullCalculated: calculatedChecksum,
      fullExpected: expectedChecksum,
    });
  }

  /**
   * Log installation start
   */
  installStart(bundlePath: string): void {
    console.log(`${this.prefix} 🔧 Installation started`, {
      bundlePath,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log installation complete
   */
  installComplete(): void {
    console.log(`${this.prefix} ✅ Installation complete - restarting app...`, {
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log status report sent to server
   */
  statusReported(
    status: OtaUpdateStatus,
    response: OtaStatusResponse,
    progress?: number,
  ): void {
    console.log(`${this.prefix} 📤 Status reported to server`, {
      status,
      progress,
      shouldRetry: response.should_retry,
      retryAfterSeconds: response.retry_after_seconds,
      message: response.message,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log error during any stage
   */
  error(
    stage: string,
    error: Error,
    errorCode?: OtaErrorCode,
    additionalData?: Record<string, unknown>,
  ): void {
    console.error(`${this.prefix} ❌ Error during ${stage}:`, {
      stage,
      errorCode: errorCode || OtaErrorCode.UNKNOWN_ERROR,
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      ...additionalData,
    });
  }

  /**
   * Log retry attempt
   */
  retry(
    stage: string,
    attemptNumber: number,
    maxAttempts: number,
    delayMs: number,
  ): void {
    console.warn(`${this.prefix} 🔄 Retrying ${stage}...`, {
      attemptNumber,
      maxAttempts,
      delayMs,
      delaySeconds: (delayMs / 1000).toFixed(1),
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log when max retries exceeded
   */
  maxRetriesExceeded(stage: string, maxAttempts: number): void {
    console.error(`${this.prefix} ❌ Max retries exceeded for ${stage}`, {
      stage,
      maxAttempts,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log download pause
   */
  downloadPaused(): void {
    console.log(`${this.prefix} ⏸️ Download paused`, {
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log download resume
   */
  downloadResumed(): void {
    console.log(`${this.prefix} ▶️ Download resumed`, {
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log when checking stops
   */
  checkingStopped(): void {
    console.log(`${this.prefix} ⏹️ Periodic checking stopped`, {
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log service destruction
   */
  destroy(): void {
    console.log(`${this.prefix} 🛑 OTA Service destroyed`, {
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log version compatibility check
   */
  versionCompatibility(
    currentVersion: string,
    minRequired: string | null,
    isCompatible: boolean,
  ): void {
    console.log(`${this.prefix} 🔍 Version compatibility check`, {
      currentVersion,
      minRequired: minRequired || 'none',
      isCompatible,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Log network status
   */
  networkStatus(online: boolean): void {
    const emoji = online ? '🌐' : '📡';
    console.log(
      `${this.prefix} ${emoji} Network status: ${online ? 'online' : 'offline'}`,
      {
        online,
        timestamp: new Date().toISOString(),
      },
    );
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  /**
   * Format bytes to human-readable string
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }
}

// Export singleton instance
export const otaLogger = new OtaLogger();

// Export class for testing
export default OtaLogger;
